import React, { useContext, useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { EditContext } from "../context/EditContext";
import { MetadataContext } from "../context/MetadataContext";
import { useDragHandle } from "../context/DragHandleContext";
import { typography, textColors, surfaces, borders, interactive } from "../../design-system/tokens";
import InlineTextEditor from "./InlineTextEditor";
import InlineFontFamilyPicker from "./InlineFontFamilyPicker";
import InlineStylePresetPicker from "./InlineStylePresetPicker";
import useBaseStyle from "../../core/hooks/useBaseStyle";
import useCharacterStyle from "../../core/hooks/useCharacterStyle";
import { apiFetch } from "../../core/utils/api";
import { getTextPositionClasses } from "../../core/utils/characterStyleUtils";
import { FONT_FAMILY_KEYS } from "../../core/utils/fontFamilyUtils";
import { generateEditUrl, extractRouteParams } from "../../core/utils/urlUtils";
import { LayoutByLayoutField } from "./fields/LayoutByLayoutField";
import WebsitePageField from "./fields/WebsitePageField";
import {
  CheckIcon,
  XIcon,
  LayoutIcon,
  TrashIcon,
  FontSizeUpIcon,
  FontSizeDownIcon,
  ExternalLinkIcon,
  PaletteIcon,
} from "../../design-system/icons";
import { FormatPainterIcon } from "../navbar/icons";
import { useEditorInteractionOptional } from "../context/EditorInteractionContext";
import { transformColor } from "../../core/utils/generalUtils";
import InlineQuickColorPicker from "./InlineQuickColorPicker";
import { SUPPORTED_ELEMENT_STYLE_PRESET_TYPES } from "./panels/elementStylePresetsApi";
import { 
  supportsInlineEditing, 
  getInlineEditableField, 
  getInlineEditValue,
  shouldEditAsHTML,
  htmlToMarkdown
} from "../utils/textUtils";

export const SuppressChildHoverBorderContext = React.createContext(false);

// Stable JSON stringify (sorted keys) to avoid referential churn causing effects to fire repeatedly.
// Used for layout syncing where the backend may return new object references on each fetch.
const stableStringify = (value) => {
  if (value === undefined) return "null";
  if (value === null) return "null";
  if (typeof value !== "object") {
    const primitive = JSON.stringify(value);
    return primitive === undefined ? "null" : primitive;
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
    .join(",")}}`;
};

const clampNumber = (value, min, max) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
};

const NON_DELETABLE_ELEMENT_TYPES = new Set([
  "navbar",
  "website_page",
  "sidebar",
  "footer",
]);

const BUTTON_LINK_CHOICES = {
  INTERNAL: "internal",
  EXTERNAL: "external",
};

const normalizeButtonLinkChoice = (element) => {
  const rawChoice = String(element?.link_choice || "").toLowerCase();
  if (
    rawChoice === BUTTON_LINK_CHOICES.INTERNAL ||
    rawChoice === BUTTON_LINK_CHOICES.EXTERNAL
  ) {
    return rawChoice;
  }
  return element?.link_external
    ? BUTTON_LINK_CHOICES.EXTERNAL
    : BUTTON_LINK_CHOICES.INTERNAL;
};

const normalizeLinkedSubdomain = (rawSubdomain) => {
  if (rawSubdomain === null || rawSubdomain === undefined) return "";
  if (typeof rawSubdomain !== "string") return null;

  const trimmed = rawSubdomain.trim();
  if (!trimmed) return "";
  return trimmed.replace(/^\/+/, "");
};

const extractLinkedPageId = (linkInternal) => {
  if (typeof linkInternal === "number" && Number.isFinite(linkInternal)) {
    return linkInternal;
  }
  if (typeof linkInternal === "string") {
    const trimmed = linkInternal.trim();
    if (/^\d+$/.test(trimmed)) {
      return Number.parseInt(trimmed, 10);
    }
    return null;
  }
  if (linkInternal && typeof linkInternal === "object") {
    return extractLinkedPageId(linkInternal.id);
  }
  return null;
};

const extractLinkedSubdomain = (linkInternal) => {
  if (typeof linkInternal === "string") {
    const trimmed = linkInternal.trim();
    if (/^\d+$/.test(trimmed)) return null;
    return normalizeLinkedSubdomain(trimmed);
  }

  if (!linkInternal || typeof linkInternal !== "object") return null;

  if (Object.prototype.hasOwnProperty.call(linkInternal, "subdomain")) {
    return normalizeLinkedSubdomain(linkInternal.subdomain);
  }
  return null;
};

const getCurrentEditRouteContext = () => {
  if (typeof window === "undefined") return null;
  const route = extractRouteParams(window.location?.pathname || "");
  if (!route?.isEdit || !route?.username || !route?.domain || !route?.versionSlug) {
    return null;
  }
  return route;
};

const formatFontFamilyLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/_/g, " ");

const DEFAULT_FONT_FAMILY_CHOICES = FONT_FAMILY_KEYS.map((value) => ({
  value,
  label: formatFontFamilyLabel(value),
}));

const isNativeInteractiveTarget = (target) => {
  if (!(target instanceof Element)) return false;
  // Match actual HTML interactive elements only.
  // Deliberately exclude ARIA role selectors ([role='button'], [role='link'])
  // because dnd-kit's useSortable adds role="button" to every sortable wrapper
  // div, which would cause false positives for all elements inside sortable contexts.
  return Boolean(
    target.closest(
      "a[href],button,input,select,textarea,label,form,summary",
    ),
  );
};

const isInlineEditableTarget = (target) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest("[contenteditable='true']") || target.isContentEditable);
};

const EditableElement = ({
  element, // The data object representing the element (component/item)
  children, // The actual element to render
  styleAsWebsiteLevel = false, // Optional: style indicator as website-level (green) while keeping selection target
  disableQuickDelete = false, // Optional: hide the top-right quick trash button for this element
  hideTopRightQuickActions = false, // Optional: suppress the top-right quick action toolbar for this element
  suppressHoverBorder = false, // Optional: only show border on selection, not on hover (useful for automatic-mode components where children already provide hover feedback)
}) => {
  /* eslint-disable react-hooks/rules-of-hooks -- all hooks are called unconditionally
     before the isEditMode early-return; false positive in eslint-plugin-react-hooks 4.x
     with large components during HMR */
  const safeElement = element || {};
  const editContext = useContext(EditContext);
  const metadataContext = useContext(MetadataContext);
  const inheritedSuppressHoverBorder = useContext(SuppressChildHoverBorderContext);
  const isEditMode = !!editContext;
  const [isHovered, setIsHovered] = useState(false);
  const [childEditableHovered, setChildEditableHovered] = useState(false);
  // When true, the mouse is over the (possibly portaled) corner chrome itself.
  // Used to keep the chrome visible while the pointer is over a quick-action
  // button that sits in a body portal (and therefore outside this element's DOM,
  // which would otherwise fire mouseleave and hide the toolbar).
  const [chromeHovered, setChromeHovered] = useState(false);
  // Set when an ancestor cover tile (overflow-hidden) would clip the corner
  // chrome. In that case the chrome is rendered through a body portal so it
  // floats on top of the media instead of being cut off at the tile edge.
  const [clipsChrome, setClipsChrome] = useState(false);
  // Viewport rect of this element, used to position the portaled chrome.
  const [chromeRect, setChromeRect] = useState(null);
  const wrapperRef = useRef(null);
  const childHoveredRef = useRef(false);
  const childHoverDebounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (childHoverDebounceRef.current) {
        clearTimeout(childHoverDebounceRef.current);
      }
    };
  }, []);

  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  const [isButtonLinkPopoverOpen, setIsButtonLinkPopoverOpen] = useState(false);
  const [layoutDraft, setLayoutDraft] = useState(safeElement.layout || null);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const layoutButtonRef = useRef(null);
  const layoutModalRef = useRef(null);
  const buttonLinkButtonRef = useRef(null);
  const buttonLinkPopoverRef = useRef(null);
  
  // Compute styles for inline editing (to preserve formatting)
  // MUST be called before any early returns (React hooks rules)
  const { styleClasses: baseStyleClasses, inlineStyles: baseInlineStyles } = useBaseStyle(safeElement);
  const { styleClasses: charStyleClasses, inlineStyles: charInlineStyles } = useCharacterStyle(safeElement);
  
  // Compute final styles for inline editor (mimics Title/Text rendering)
  // This uses the style hooks above and must be before early returns
  const inlineEditorStyles = useMemo(() => {
    // For Title elements
    if (safeElement.type === 'title') {
      // Get text position classes
      const textPosition = safeElement.character_style?.text_position || safeElement.characterStyle?.text_position;
      const headingPositionClasses = getTextPositionClasses(textPosition, false);
      
      // Remove positioning classes from characterStyleClasses since we handle them separately
      const cleanCharacterClasses = charStyleClasses
        .split(" ")
        .filter((c) => !/^(flex|justify-|items-|text-left|text-right|text-center|text-justify)/.test(c))
        .join(" ");
      
      // Combine all classes (base + character + positioning)
      const headingClasses = `${baseStyleClasses} ${cleanCharacterClasses} ${headingPositionClasses}`
        .split(" ")
        .filter(c => c && c.trim() !== "" && c.trim() !== "none")
        .join(" ");
      
      const finalStyle = { ...baseInlineStyles, ...charInlineStyles };
      
      return { className: headingClasses, style: finalStyle };
    }
    
    // For Text elements
    if (safeElement.type === 'text') {
      // Mirror the renderer (Text.js): drop the flex-row positioning classes
      // injected by useCharacterStyle (they would conflict with the text
      // wrapper's flex-col layout and visually misalign paragraphs) and
      // re-derive only the text-align from text_position.
      const textPosition =
        safeElement.character_style?.text_position ||
        safeElement.characterStyle?.text_position ||
        "middle-full";
      const textAlignClass = getTextPositionClasses(textPosition, false);

      const cleanCharClasses = charStyleClasses
        .split(" ")
        .filter(
          (c) =>
            !/^(flex|justify-|items-|text-left|text-right|text-center|text-justify)$/.test(
              c,
            ),
        )
        .join(" ");

      let finalClasses = `${baseStyleClasses} ${cleanCharClasses} ${textAlignClass}`.trim();
      // Normalize white/color classes so Tailwind hover utilities work
      finalClasses = finalClasses
        .split(" ")
        .map((c) =>
          c
            .replace(/^hover:text-white(-\d+)?$/, "hover:text-white")
            .replace(/^text-white(-\d+)?$/, "text-white"),
        )
        .join(" ");
      
      // Merge inline styles
      const finalStyles = { ...baseInlineStyles, ...charInlineStyles };
      
      return { className: finalClasses, style: finalStyles };
    }
    
    return { className: '', style: {} };
  }, [safeElement, baseStyleClasses, baseInlineStyles, charStyleClasses, charInlineStyles]);

  const { 
    selectElement, 
    selectedElement, 
    startInlineEdit, 
    stopInlineEdit, 
    isInlineEditing,
    requestEditPanelFocus,
    updateField,
    deleteElement,
    websiteId,
    applyStylePreset,
    formatPainter,
    armFormatPainter,
    clearFormatPainter,
    applyFormatPaint,
    isFormatPainterEligible,
  } = editContext || {};
  // Must match BOTH id and type to avoid cross-table id collisions (common in Django models)
  const isSelected =
    isEditMode &&
    selectedElement?.id === safeElement.id &&
    selectedElement?.type === safeElement.type;
  
  // Use selectedElement for optimistic updates if it matches this element
  // This ensures we show the new text immediately while save is in progress.
  // We merge safeElement (full data from the renderer) with selectedElement
  // (which may be minimal when selected from the Internal Elements tab) so
  // the child component always receives a complete item object while still
  // picking up any optimistic field overrides from selectedElement.
  const displayElement = isSelected && selectedElement ? { ...safeElement, ...selectedElement } : safeElement;
  const isSubmitButton = !!(displayElement?.is_submit_button ?? safeElement?.is_submit_button);
  const showLayoutQuickEdit =
    isEditMode &&
    safeElement.type === "component" &&
    safeElement.manual_layout !== false;
  const showQuickDelete =
    isEditMode &&
    !styleAsWebsiteLevel &&
    !disableQuickDelete &&
    typeof deleteElement === "function" &&
    safeElement?.id != null &&
    !!safeElement?.type &&
    !NON_DELETABLE_ELEMENT_TYPES.has(safeElement.type);
  const showQuickTextTools =
    isEditMode &&
    isSelected &&
    (safeElement.type === "title" || safeElement.type === "text") &&
    typeof updateField === "function";
  const showQuickStylePresetTools =
    isEditMode &&
    isSelected &&
    SUPPORTED_ELEMENT_STYLE_PRESET_TYPES.has(safeElement.type) &&
    typeof applyStylePreset === "function";
  const LINK_EDITABLE_TYPES = new Set(["button", "navbar_section", "sidebar_section"]);
  const showQuickButtonLinkTools =
    isEditMode &&
    isSelected &&
    LINK_EDITABLE_TYPES.has(safeElement.type) &&
    !(safeElement.type === "button" && isSubmitButton) &&
    typeof updateField === "function";
  const showQuickFormatPainter =
    isEditMode &&
    isSelected &&
    typeof armFormatPainter === "function" &&
    isFormatPainterEligible?.(safeElement);
  const formatPainterArmedHere = !!(
    formatPainter?.active &&
    formatPainter?.source?.id === safeElement.id &&
    formatPainter?.source?.type === safeElement.type
  );
  const formatPainterStickyHere = formatPainterArmedHere && !!formatPainter?.sticky;
  const showTopRightQuickActions =
    !hideTopRightQuickActions &&
    (
      showLayoutQuickEdit ||
      showQuickDelete ||
      showQuickTextTools ||
      showQuickStylePresetTools ||
      showQuickButtonLinkTools ||
      showQuickFormatPainter
    );
  const layoutBaseline = displayElement?.layout ?? null;
  const hasLayoutChanges = useMemo(() => {
    const currentString = JSON.stringify(layoutBaseline ?? null);
    const draftString = JSON.stringify(layoutDraft ?? null);
    return currentString !== draftString;
  }, [layoutBaseline, layoutDraft]);

  const closeQuickLayout = useCallback(() => {
    setIsLayoutModalOpen(false);
    setIsSavingLayout(false);
    setLayoutDraft(layoutBaseline ?? null);
  }, [layoutBaseline]);

  const closeQuickButtonLinkPopover = useCallback(() => {
    setIsButtonLinkPopoverOpen(false);
  }, []);

  const handleQuickLayoutSave = useCallback(() => {
    console.log('[QuickLayout] Save clicked:', {
      hasLayoutChanges,
      showLayoutQuickEdit,
      layoutDraft,
      layoutBaseline,
      updateFieldAvailable: !!updateField,
    });
    
    if (!hasLayoutChanges || !showLayoutQuickEdit || !updateField) {
      console.log('[QuickLayout] Cannot save - missing requirements');
      closeQuickLayout();
      return;
    }
    
    console.log('[QuickLayout] Calling updateField with layout:', layoutDraft);
    setIsSavingLayout(true);
    
    // Call updateField - it handles autosave internally
    updateField("layout", layoutDraft || {});
    
    // Close modal after a brief delay to allow the save to be initiated
    setTimeout(() => {
      console.log('[QuickLayout] Closing modal after save');
      setIsLayoutModalOpen(false);
      setIsSavingLayout(false);
    }, 100);
  }, [closeQuickLayout, hasLayoutChanges, layoutDraft, layoutBaseline, showLayoutQuickEdit, updateField]);

  const handleLayoutButtonClick = useCallback((event) => {
    event.stopPropagation();
    if (!isEditMode) return;
    if (!isSelected && selectElement) {
      selectElement(element);
    }
    setLayoutDraft(layoutBaseline ?? {});
    setIsLayoutModalOpen((prev) => !prev);
  }, [element, isEditMode, isSelected, layoutBaseline, selectElement]);

  const handleDeleteButtonClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!showQuickDelete || !deleteElement) return;
      deleteElement(safeElement);
    },
    [deleteElement, safeElement, showQuickDelete],
  );

  const currentButtonLinkChoice = useMemo(
    () => normalizeButtonLinkChoice(displayElement),
    [displayElement],
  );
  const currentButtonExternalUrl = displayElement?.link_external || "";
  const currentButtonInternalPage = displayElement?.link_internal || null;
  const buttonWebsiteId = websiteId || displayElement?.website || safeElement?.website || null;
  const editRouteContext = useMemo(() => getCurrentEditRouteContext(), []);
  const [linkedPageOptions, setLinkedPageOptions] = useState([]);

  const linkedSubdomainFromSelection = useMemo(
    () =>
      currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL
        ? extractLinkedSubdomain(currentButtonInternalPage)
        : null,
    [currentButtonInternalPage, currentButtonLinkChoice],
  );
  const linkedPageId = useMemo(
    () =>
      currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL
        ? extractLinkedPageId(currentButtonInternalPage)
        : null,
    [currentButtonInternalPage, currentButtonLinkChoice],
  );
  const shouldResolveLinkedPageById =
    currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL &&
    linkedSubdomainFromSelection === null &&
    linkedPageId !== null &&
    !!buttonWebsiteId;

  useEffect(() => {
    if (!shouldResolveLinkedPageById) {
      setLinkedPageOptions([]);
      return;
    }

    let isCancelled = false;

    const fetchLinkedPages = async () => {
      try {
        const response = await apiFetch(`/api/websites/${buttonWebsiteId}/pages/?mode=skeleton`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!isCancelled) {
          setLinkedPageOptions(Array.isArray(data) ? data : []);
        }
      } catch (_) {
        if (!isCancelled) {
          setLinkedPageOptions([]);
        }
      }
    };

    fetchLinkedPages();

    return () => {
      isCancelled = true;
    };
  }, [buttonWebsiteId, shouldResolveLinkedPageById]);

  const resolvedLinkedSubdomain = useMemo(() => {
    if (currentButtonLinkChoice !== BUTTON_LINK_CHOICES.INTERNAL) return null;
    if (linkedSubdomainFromSelection !== null) return linkedSubdomainFromSelection;
    if (linkedPageId === null || !Array.isArray(linkedPageOptions)) return null;

    const linkedPage = linkedPageOptions.find(
      (page) => Number(page?.id) === Number(linkedPageId),
    );
    if (!linkedPage) return null;
    return normalizeLinkedSubdomain(linkedPage.subdomain);
  }, [
    currentButtonLinkChoice,
    linkedSubdomainFromSelection,
    linkedPageId,
    linkedPageOptions,
  ]);

  const internalLinkTarget = useMemo(() => {
    if (currentButtonLinkChoice !== BUTTON_LINK_CHOICES.INTERNAL) return null;
    if (!editRouteContext || resolvedLinkedSubdomain === null) return null;

    return {
      subdomain: resolvedLinkedSubdomain,
      url: generateEditUrl(
        editRouteContext.username,
        editRouteContext.domain,
        editRouteContext.versionSlug,
        resolvedLinkedSubdomain,
      ),
    };
  }, [currentButtonLinkChoice, editRouteContext, resolvedLinkedSubdomain]);

  const normalizedExternalUrl = useMemo(() => {
    const trimmed = String(currentButtonExternalUrl || "").trim();
    return trimmed || null;
  }, [currentButtonExternalUrl]);

  const buttonLinkAction = useMemo(() => {
    if (currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL) {
      if (internalLinkTarget?.url) {
        return {
          label: internalLinkTarget.subdomain ? "Go to page" : "Go to main page",
          title: internalLinkTarget.subdomain
            ? `Go to /${internalLinkTarget.subdomain}`
            : "Go to main page",
          disabled: false,
        };
      }
      return {
        label: "Go to page",
        title:
          linkedPageId !== null ? "Resolving linked page..." : "Select an internal page first",
        disabled: true,
      };
    }

    return {
      label: "Open link",
      title: normalizedExternalUrl
        ? `Open ${normalizedExternalUrl}`
        : "Enter an external URL first",
      disabled: !normalizedExternalUrl,
    };
  }, [currentButtonLinkChoice, internalLinkTarget, linkedPageId, normalizedExternalUrl]);

  const handleButtonLinkButtonClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!showQuickButtonLinkTools) return;
      setIsButtonLinkPopoverOpen((prev) => !prev);
    },
    [showQuickButtonLinkTools],
  );

  const handleQuickButtonLinkChoiceChange = useCallback(
    (event) => {
      const nextChoice = String(event?.target?.value || "").toLowerCase();
      if (!showQuickButtonLinkTools || !updateField) return;
      if (
        nextChoice !== BUTTON_LINK_CHOICES.INTERNAL &&
        nextChoice !== BUTTON_LINK_CHOICES.EXTERNAL
      ) {
        return;
      }
      updateField("link_choice", nextChoice);
    },
    [showQuickButtonLinkTools, updateField],
  );

  const handleQuickButtonExternalChange = useCallback(
    (event) => {
      if (!showQuickButtonLinkTools || !updateField) return;
      updateField("link_external", event?.target?.value || "");
    },
    [showQuickButtonLinkTools, updateField],
  );

  const handleQuickButtonInternalChange = useCallback(
    (nextPage) => {
      if (!showQuickButtonLinkTools || !updateField) return;
      updateField("link_internal", nextPage);
    },
    [showQuickButtonLinkTools, updateField],
  );

  const handleQuickButtonOpenTarget = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL) {
        if (!internalLinkTarget?.url) return;
        closeQuickButtonLinkPopover();
        window.history.pushState({}, "", internalLinkTarget.url);
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }

      if (!normalizedExternalUrl) return;
      window.open(normalizedExternalUrl, "_blank", "noopener,noreferrer");
    },
    [
      closeQuickButtonLinkPopover,
      currentButtonLinkChoice,
      internalLinkTarget,
      normalizedExternalUrl,
    ],
  );

  const quickButtonLinkPopoverStyle = useMemo(() => {
    const viewportPadding = 8;
    const estimatedHeight =
      currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL ? 320 : 260;

    if (typeof window === "undefined") {
      return {
        zIndex: 9999,
        top: `${viewportPadding}px`,
        right: `${viewportPadding}px`,
      };
    }

    const rect = buttonLinkButtonRef.current?.getBoundingClientRect?.();
    if (!rect) {
      return {
        zIndex: 9999,
        top: `${viewportPadding}px`,
        right: `${viewportPadding}px`,
      };
    }

    return {
      zIndex: 9999,
      top: `${Math.max(
        viewportPadding,
        Math.min(rect.bottom + 8, window.innerHeight - estimatedHeight - viewportPadding),
      )}px`,
      right: `${Math.max(viewportPadding, window.innerWidth - rect.right)}px`,
    };
  }, [currentButtonLinkChoice, isButtonLinkPopoverOpen]);

  // When the quick-layout modal is closed, keep the draft in sync with the baseline layout.
  // IMPORTANT: use a stable signature so we don't loop when layout objects are referentially new
  // but content-identical across renders.
  const layoutBaselineSig = useMemo(() => stableStringify(layoutBaseline ?? null), [layoutBaseline]);

  useEffect(() => {
    if (isLayoutModalOpen) return;
    // Only update when layout content changes (signature changes).
    // Parse to avoid depending on the layout object reference.
    try {
      setLayoutDraft(JSON.parse(layoutBaselineSig));
    } catch (e) {
      // Fallback: keep draft null if signature isn't valid JSON (shouldn't happen)
      setLayoutDraft(null);
    }
  }, [isLayoutModalOpen, layoutBaselineSig]);

  useEffect(() => {
    if (showQuickButtonLinkTools) return;
    closeQuickButtonLinkPopover();
  }, [closeQuickButtonLinkPopover, showQuickButtonLinkTools]);

  useEffect(() => {
    if (!isLayoutModalOpen) return;

    const handleClick = (event) => {
      if (
        layoutModalRef.current &&
        layoutModalRef.current.contains(event.target)
      ) {
        return;
      }
      if (layoutButtonRef.current && layoutButtonRef.current.contains(event.target)) {
        return;
      }
      closeQuickLayout();
    };

    const handleKey = (event) => {
      if (event.key === "Escape") {
        closeQuickLayout();
      }
    };

    // Capture phase: sibling quick-action triggers (style preset, font picker, etc.)
    // call event.stopPropagation(), which in React also halts the native event before
    // it bubbles to document. Capture fires before those handlers, so we still detect
    // the outside click and can close this modal.
    document.addEventListener("mousedown", handleClick, true);
    document.addEventListener("touchstart", handleClick, true);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick, true);
      document.removeEventListener("touchstart", handleClick, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [closeQuickLayout, isLayoutModalOpen]);

  useEffect(() => {
    if (!isButtonLinkPopoverOpen) return;

    const handleClick = (event) => {
      if (
        buttonLinkPopoverRef.current &&
        buttonLinkPopoverRef.current.contains(event.target)
      ) {
        return;
      }
      if (
        buttonLinkButtonRef.current &&
        buttonLinkButtonRef.current.contains(event.target)
      ) {
        return;
      }
      closeQuickButtonLinkPopover();
    };

    const handleKey = (event) => {
      if (event.key === "Escape") {
        closeQuickButtonLinkPopover();
      }
    };

    // Capture phase: sibling quick-action triggers (style preset, font picker, etc.)
    // call event.stopPropagation(), which in React also halts the native event before
    // it bubbles to document. Capture fires before those handlers, so we still detect
    // the outside click and can close this popover.
    document.addEventListener("mousedown", handleClick, true);
    document.addEventListener("touchstart", handleClick, true);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick, true);
      document.removeEventListener("touchstart", handleClick, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [closeQuickButtonLinkPopover, isButtonLinkPopoverOpen]);
  
  // The corner chrome (type indicator, quick-action toolbar, drag handle) is
  // shown when the element is hovered or selected. We also keep it "active" while
  // the pointer is over the chrome itself (chromeHovered) so a portaled toolbar
  // doesn't disappear the moment the cursor leaves the element's DOM box.
  const chromeActive = isEditMode && (isHovered || isSelected || chromeHovered);

  // Detect whether a nearby ancestor clips this element's overflow. Cover/
  // full-bleed component modes wrap media tiles in `overflow-hidden` so the image
  // fills and crops to the tile; that same clip cuts off the corner chrome
  // (indicator/toolbar/drag handle), which the user wants floating on top. When a
  // clipping ancestor is found between this element and its parent editable, we
  // portal the chrome out so it can overflow the tile. The walk is className-based
  // (reliable without computed CSS, e.g. in jsdom) and bounded so ordinary
  // elements — whose nearest clipping context is the scroll container, not a tight
  // cover tile — never trigger the portal.
  useEffect(() => {
    if (!chromeActive) {
      setClipsChrome(false);
      return;
    }
    const node = wrapperRef.current;
    if (!node) {
      setClipsChrome(false);
      return;
    }
    const CLIP_CLASS = /(?:^|\s)overflow-(?:hidden|clip|x-hidden|y-hidden|x-clip|y-clip)(?:\s|$)/;
    let ancestor = node.parentElement;
    let depth = 0;
    let clipped = false;
    while (ancestor && ancestor !== document.body && depth < 8) {
      // Clipping beyond the parent editable element is that element's concern.
      if (ancestor.matches?.("[data-editable-type]")) break;
      const cls = typeof ancestor.className === "string" ? ancestor.className : "";
      if (CLIP_CLASS.test(cls)) {
        clipped = true;
        break;
      }
      ancestor = ancestor.parentElement;
      depth += 1;
    }
    setClipsChrome(clipped);
  }, [chromeActive]);

  // While the chrome is portaled, keep its fixed position synced to the element
  // (scroll/resize/layout changes). Listeners are only attached when needed.
  useEffect(() => {
    if (!clipsChrome || !chromeActive) {
      setChromeRect(null);
      return;
    }
    const updateRect = () => {
      const rect = wrapperRef.current?.getBoundingClientRect?.();
      if (rect) {
        setChromeRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    };
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && wrapperRef.current) {
      resizeObserver = new ResizeObserver(updateRect);
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [clipsChrome, chromeActive]);

  // Check if this element supports inline editing
  const canInlineEdit = isEditMode && supportsInlineEditing(safeElement.type);
  const editableField = canInlineEdit ? getInlineEditableField(safeElement.type) : null;
  const isCurrentlyInlineEditing = canInlineEdit && editableField && isInlineEditing?.(safeElement, editableField);

  // Helper function to determine if an element is website-level
  const isWebsiteLevelElement = (element) => {
    const websiteLevelTypes = ["navbar", "sidebar", "footer", "navbar_section", "sidebar_section"];
    return websiteLevelTypes.includes(element?.type);
  };

  // Helper function to format type names:
  // - replace underscores with spaces
  // - Title Case each word (e.g. `navbar_section` -> `Navbar Section`)
  const formatTypeName = (type) => {
    const formatted = String(type || "component").replace(/_/g, " ").trim();
    return formatted
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Read drag-anywhere flag and listeners from context (set by SortableItemWrapper parents).
  // MUST be declared before any early return to satisfy React hooks rules.
  // When dragAnywhereEnabled is true, listeners are forwarded to the root div so the user can
  // initiate a drag by clicking and moving anywhere on the element (not just a small handle icon).
  const dragCtx = useDragHandle();
  const dragAnywhereEnabled = !!(dragCtx?.dragAnywhereEnabled);
  const dragListeners = dragCtx?.dragListeners ?? null;
  const editorInteraction = useEditorInteractionOptional();

  // Precompute quick-action classes and handlers BEFORE any early return to satisfy hook ordering.
  const isWebsiteLevel = styleAsWebsiteLevel ? true : isWebsiteLevelElement(safeElement);
  const isSection = safeElement.type?.includes('section');
  const isSidebar = safeElement.type === 'sidebar';
  const quickActionButtonBaseClass = `
    flex items-center justify-center p-1.5 rounded border shadow-md
    ${borders.default}
    ${surfaces.elevated}
  `;
  const layoutQuickActionButtonClass = `
    ${quickActionButtonBaseClass}
    text-blue-600 dark:text-blue-400
    hover:bg-blue-100 hover:text-blue-800 dark:hover:bg-blue-900 dark:hover:text-blue-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
  `;
  const deleteQuickActionButtonClass = `
    ${quickActionButtonBaseClass}
    text-red-600 dark:text-red-400
    hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900 dark:hover:text-red-200
    focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400
  `;
  const buttonLinkQuickActionButtonClass = `
    ${quickActionButtonBaseClass}
    text-gray-700 dark:text-gray-200
    hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-dark-700 dark:hover:text-white
    focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-300
  `;
  const stylePresetQuickActionButtonClass = `
    ${quickActionButtonBaseClass}
    text-violet-600 dark:text-violet-400
    hover:bg-violet-100 hover:text-violet-800 dark:hover:bg-violet-900 dark:hover:text-violet-200
    focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400
  `;
  const formatPainterQuickActionButtonClass = `
    ${quickActionButtonBaseClass}
    text-gray-900 dark:text-gray-100
    hover:bg-gray-100 hover:text-black dark:hover:bg-dark-700 dark:hover:text-white
    focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-300
  `;
  const formatPainterActiveButtonClass = `
    flex items-center justify-center p-1.5 rounded border shadow-md
    bg-amber-100 dark:bg-amber-900/40
    border-amber-400 dark:border-amber-500
    text-amber-700 dark:text-amber-300
    hover:bg-amber-200 dark:hover:bg-amber-800/50
    focus:outline-none focus:ring-2 focus:ring-amber-400 dark:focus:ring-amber-500
    animate-pulse
  `;
  // For the "grouped" text tools pill, each inner button is borderless; the wrapper
  // carries the border/shadow so it matches the trash button.
  const textToolsGroupClass = `
    flex items-center overflow-hidden rounded border shadow-md
    ${borders.default}
    ${surfaces.elevated}
  `;
  const textToolsInnerButtonClass = `
    flex items-center justify-center p-1.5
    text-gray-700 dark:text-gray-200
    hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-dark-700 dark:hover:text-white
    focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-300
  `;
  const textToolsDividerClass = `w-px self-stretch ${borders.default}`;

  const buildNextCharacterStyle = useCallback(
    (patch = {}) => {
      const current =
        displayElement?.character_style ||
        displayElement?.characterStyle ||
        selectedElement?.character_style ||
        selectedElement?.characterStyle ||
        {};
      const next = { ...current, ...patch };
      // Preserve font_size merge semantics if patch includes it
      if (patch?.font_size && typeof patch.font_size === "object") {
        next.font_size = { ...(current.font_size || {}), ...patch.font_size };
      }
      return next;
    },
    [displayElement, selectedElement],
  );

  const getCurrentTextCharacterStyle = useCallback(() => {
    return (
      displayElement?.character_style ||
      displayElement?.characterStyle ||
      selectedElement?.character_style ||
      selectedElement?.characterStyle ||
      {}
    );
  }, [displayElement, selectedElement]);

  const getCurrentTextColorHex = useCallback(() => {
    const current = getCurrentTextCharacterStyle();
    const raw = current?.text_color || "#111827";
    try {
      const hex = transformColor(raw, 100, "hex");
      return typeof hex === "string" && hex.startsWith("#") ? hex : "#111827";
    } catch (_) {
      return "#111827";
    }
  }, [getCurrentTextCharacterStyle]);

  const getCurrentTextFontFamily = useCallback(() => {
    const current = getCurrentTextCharacterStyle();
    const raw = current?.font_family;
    return typeof raw === "string" && raw.trim() ? raw.trim() : "sans";
  }, [getCurrentTextCharacterStyle]);

  const textFontFamilyChoices = useMemo(() => {
    const metadataKey =
      safeElement.type === "title" ? "title" : safeElement.type === "text" ? "text" : null;
    const metadataChoices =
      metadataKey &&
      metadataContext?.[metadataKey]?.main_fields?.character_style?.nested_fields?.font_family?.choices;

    const normalizedChoices =
      Array.isArray(metadataChoices) && metadataChoices.length > 0
        ? metadataChoices
            .map((choice) => {
              const value = String(choice?.value || "").trim();
              if (!value) return null;
              return {
                value,
                label: String(choice?.label || formatFontFamilyLabel(value)).trim() || value,
              };
            })
            .filter(Boolean)
        : DEFAULT_FONT_FAMILY_CHOICES;

    const currentFontFamily = getCurrentTextFontFamily();
    if (!currentFontFamily) return normalizedChoices;

    const hasCurrentChoice = normalizedChoices.some(
      (choice) => choice?.value === currentFontFamily,
    );
    if (hasCurrentChoice) return normalizedChoices;

    return [
      ...normalizedChoices,
      {
        value: currentFontFamily,
        label: formatFontFamilyLabel(currentFontFamily),
      },
    ];
  }, [getCurrentTextFontFamily, metadataContext, safeElement.type]);

  const adjustFontSizeAllBreakpoints = useCallback(
    (delta) => {
      if (!showQuickTextTools || !updateField) return;

      const current =
        displayElement?.character_style ||
        displayElement?.characterStyle ||
        selectedElement?.character_style ||
        selectedElement?.characterStyle ||
        {};
      const fs = current?.font_size || {};

      const step = clampNumber(delta, -50, 50);
      const nextFontSize = {
        ...(typeof fs === "object" ? fs : {}),
        // clamp to sane visual bounds
        extra_small: clampNumber((fs?.extra_small ?? 16) + step, 8, 200),
        small: clampNumber((fs?.small ?? 16) + step, 8, 200),
        medium: clampNumber((fs?.medium ?? 16) + step, 8, 200),
        large: clampNumber((fs?.large ?? 16) + step, 8, 200),
        extra_large: clampNumber((fs?.extra_large ?? 16) + step, 8, 200),
      };

      const nextCharStyle = buildNextCharacterStyle({ font_size: nextFontSize });
      updateField("character_style", nextCharStyle);
    },
    [buildNextCharacterStyle, displayElement, selectedElement, showQuickTextTools, updateField],
  );

  const handleQuickColorChange = useCallback(
    (hex) => {
      if (!showQuickTextTools || !updateField) return;
      if (!hex) return;
      const nextCharStyle = buildNextCharacterStyle({ text_color: hex });
      updateField("character_style", nextCharStyle);
    },
    [buildNextCharacterStyle, showQuickTextTools, updateField],
  );

  const handleQuickFontFamilyChange = useCallback(
    (fontFamily) => {
      if (!showQuickTextTools || !updateField) return;
      if (!fontFamily) return;
      const nextCharStyle = buildNextCharacterStyle({ font_family: fontFamily });
      updateField("character_style", nextCharStyle);
    },
    [buildNextCharacterStyle, showQuickTextTools, updateField],
  );

  const handleFormatPainterClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event?.detail > 1) return;
      if (formatPainterArmedHere) {
        clearFormatPainter?.();
        return;
      }
      if (!showQuickFormatPainter) return;
      armFormatPainter?.(safeElement, { sticky: false });
    },
    [formatPainterArmedHere, showQuickFormatPainter, armFormatPainter, clearFormatPainter, safeElement],
  );

  const handleFormatPainterDoubleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (formatPainterArmedHere) {
        if (formatPainterStickyHere) {
          clearFormatPainter?.();
        } else {
          armFormatPainter?.(safeElement, { sticky: true });
        }
        return;
      }
      if (!showQuickFormatPainter) return;
      armFormatPainter?.(safeElement, { sticky: true });
    },
    [formatPainterArmedHere, formatPainterStickyHere, showQuickFormatPainter, armFormatPainter, clearFormatPainter, safeElement],
  );

  /* eslint-enable react-hooks/rules-of-hooks */

  if (!isEditMode) {
    return children;
  }

  // Suppress parent hover border when a child EditableElement is hovered,
  // avoiding double-border "shadow" in side-by-side layouts like Split/Alternating.
  const effectiveHovered = isHovered && !childEditableHovered;

  const showHoverBorder = effectiveHovered && !suppressHoverBorder && !inheritedSuppressHoverBorder;
  const colors = isWebsiteLevel 
    ? {
        border: isSelected ? "border-green-500" : (showHoverBorder ? "border-green-400" : "border-green-400/0"),
        indicator: "bg-green-500",
      }
    : {
        border: isSelected ? "border-blue-600" : (showHoverBorder ? "border-blue-400" : "border-blue-400/0"), 
        indicator: "bg-blue-600",
      };

  // Get positioning for section indicators
  const getIndicatorPosition = () => {
    if (isSection) {
      // Section elements (navbar/sidebar sections) are typically very small, so the
      // quick-action toolbar is rendered below the element instead of inside it.
      // Push the type label beneath that toolbar so the two don't overlap.
      return `top-full right-0 ${showTopRightQuickActions ? "mt-10" : "mt-1"} z-[450]`;
    }
    // Website-level elements (navbar, sidebar, footer, and elements explicitly styled
    // as website-level such as the navbar title) keep the label inside the top-right
    // corner — their layout/chrome differs from regular page content.
    // z-[50] keeps the label above ContainerEffectOverlay (z-[5]) and the navbar (z-30)
    // when page stacking contexts are removed in edit mode.
    if (isWebsiteLevel) {
      return "top-0 right-0 m-2 z-[50]";
    }
    // Regular page content elements: place the type label just ABOVE the top border
    // line (right-aligned, touching the line from above) so it no longer overlaps the
    // top-right quick-action toolbar (e.g. the trash icon) and stays clearly readable.
    return "bottom-full right-0 z-[50]";
  };

  const handleClick = (e) => {
    // Allow resize handles and interactive crop controls to capture events
    const target = e.target;
    const isResizeHandle = target.closest('[data-handle]') || target.closest('[data-edge]') || target.closest('[data-overlay-active]');
    const isCropControl = target.closest('[data-crop-control]');
    
    if (isResizeHandle || isCropControl) {
      // Let the handle/control handle the event - don't stop propagation
      return;
    }

    // In edit mode, prevent native link/button/form actions on canvas elements.
    // This avoids accidental navigation/submission when users click or begin dragging.
    if (!isInlineEditableTarget(target) && isNativeInteractiveTarget(target)) {
      e.preventDefault();
    }
    
    e.stopPropagation();

    // Format painter: when armed, click on canvas elements applies the
    // copied style/spacing instead of selecting the element. We don't
    // re-select the target — the user is "painting", not "editing".
    if (
      isEditMode &&
      formatPainter?.active &&
      isFormatPainterEligible?.(safeElement) &&
      applyFormatPaint
    ) {
      e.preventDefault();
      try {
        applyFormatPaint(safeElement);
      } catch (err) {
        console.error("[EditableElement] applyFormatPaint failed:", err);
      }
      return;
    }

    // Single click just selects the element
    if (isEditMode && selectElement) {
      selectElement(safeElement);
      // Tutorial Step 1 gate: dispatch a reliable signal on real element clicks.
      // This is intentionally event-based (not selection-based) so it also works when users click
      // an already-selected element (React state may not change).
      try {
        if (typeof window !== "undefined" && safeElement?.id && safeElement?.type) {
          window.dispatchEvent(
            new CustomEvent("editor:tutorial-element-clicked", {
              detail: {
                id: safeElement.id,
                type: safeElement.type,
                isWebsiteLevel,
                ts: Date.now(),
              },
            }),
          );
        }
      } catch (_) {}
    }
  };

  const handleDoubleClick = (e) => {
    // Allow resize handles and interactive crop controls to capture events
    const target = e.target;
    const isResizeHandle = target.closest('[data-handle]') || target.closest('[data-edge]') || target.closest('[data-overlay-active]');
    const isCropControl = target.closest('[data-crop-control]');
    
    if (isResizeHandle || isCropControl) {
      return;
    }

    if (!isInlineEditableTarget(target) && isNativeInteractiveTarget(target)) {
      e.preventDefault();
    }
    
    e.stopPropagation();

    // For Text items, double-click should jump to the "content" field in the EditPanel
    // (instead of starting inline editing on the canvas).
    if (isEditMode && safeElement.type === "text") {
      if (requestEditPanelFocus) {
        requestEditPanelFocus(safeElement, "content", { selectAll: true });
      } else if (selectElement) {
        selectElement(safeElement);
      }
      return;
    }
    
    // Double-click on supported inline elements (currently Title only) starts inline editing
    if (isEditMode && canInlineEdit && editableField && !isCurrentlyInlineEditing && startInlineEdit) {
      console.log('[EditableElement] Starting inline edit for', safeElement.type);
      startInlineEdit(safeElement, editableField);
    }
  };
  
  const handleSaveInlineEdit = (newValue) => {
    if (!editableField) return;
    
    // Convert HTML to markdown if editing Text items
    let valueToSave = newValue;
    const originalHTML = newValue; // Keep the HTML for optimistic display
    
    if (shouldEditAsHTML(safeElement.type)) {
      valueToSave = htmlToMarkdown(newValue);
    }
    
    // For Text items, we need special handling because rendered_content is computed from content
    // We'll pass an object with both content and rendered_content for optimistic updates
    if (!isEditMode || !updateField) {
      stopInlineEdit?.();
      return;
    }

    if (safeElement.type === 'text' && editableField === 'content') {
      // Pass the markdown to the backend, but also include the HTML for optimistic display
      // The backend will ignore rendered_content and compute its own, but we use it locally
      updateField(editableField, valueToSave, { _optimistic_rendered_content: originalHTML });
    } else {
      // For other fields (like title.text), just update the single field
      updateField(editableField, valueToSave);
    }
    
    // Stop inline editing immediately to show the optimistically updated content
    stopInlineEdit?.();
  };
  
  const handleCancelInlineEdit = () => {
    stopInlineEdit?.();
  };

  const formatPainterArmedForThis =
    isEditMode &&
    !!formatPainter?.active &&
    isFormatPainterEligible?.(safeElement);
  const formatPainterIsSource =
    formatPainterArmedForThis &&
    formatPainter?.source &&
    formatPainter.source.id === safeElement.id &&
    formatPainter.source.type === safeElement.type;

  const shouldPropagateSuppress = suppressHoverBorder || inheritedSuppressHoverBorder;

  // Tight click target for images: the EditableElement wrapper is `w-full`, but an
  // image only occupies `max_width_pct_component` of the row (centered/positioned).
  // Without this, the whole row width/height is clickable & shows the selection
  // border. By making the wrapper `pointer-events: none` and the rendered image box
  // `pointer-events: auto` (set in Image.js), only the image (its width "plus a bit")
  // is interactive. `pointer-events` is inherited, so the empty side areas fall
  // through; clicks on the image still bubble to these handlers and React still
  // simulates onMouseEnter/onMouseLeave on this wrapper from the image descendant.
  const isTightClickMedia = isEditMode && safeElement.type === "image";

  // Wraps the corner chrome (type indicator, quick-action toolbar, drag handle).
  // Normally the chrome renders inline, absolutely positioned within this element.
  // But when an ancestor cover tile clips overflow (full-bleed/mosaic/gallery
  // modes), inline chrome gets cut off at the tile edge. In that case we portal
  // the chrome to <body> inside a fixed-position box matching this element's rect,
  // so it floats on top of the media and can overflow the tile freely. The box is
  // pointer-events:none so hovering the media still reaches this element; the
  // interactive chrome inside re-enables pointer events and reports its own hover
  // via chromeHovered so the toolbar doesn't vanish while the cursor is on it.
  // Plain render function (NOT a component used as <ChromeLayer/>): defining a
  // component inline and mounting it via JSX would give it a new identity each
  // render and remount the toolbar/popovers. Calling a function keeps the returned
  // element type stable (always a fragment, or always a portal <div>) so children
  // reconcile normally; only flipping clipsChrome remounts them (acceptable).
  const renderChromeLayer = (children) => {
    if (clipsChrome && chromeRect) {
      return createPortal(
        <div
          style={{
            position: "fixed",
            top: chromeRect.top,
            left: chromeRect.left,
            width: chromeRect.width,
            height: chromeRect.height,
            zIndex: 200,
            pointerEvents: "none",
          }}
        >
          {children}
        </div>,
        document.body
      );
    }
    return <>{children}</>;
  };

  const wrapperJsx = (
    <div
      data-editable-type={safeElement?.type}
      data-editable-id={safeElement?.id}
      ref={wrapperRef}
      data-format-painter-target={formatPainterArmedForThis && !formatPainterIsSource ? "1" : "0"}
      data-format-painter-source={formatPainterIsSource ? "1" : "0"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        if (childHoverDebounceRef.current) {
          clearTimeout(childHoverDebounceRef.current);
          childHoverDebounceRef.current = null;
        }
        childHoveredRef.current = false;
        setChildEditableHovered(false);
      }}
      onMouseOver={(e) => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const closestEditable = e.target.closest('[data-editable-type]');
        const isChild = closestEditable !== null && closestEditable !== wrapper && wrapper.contains(closestEditable);
        if (isChild) {
          if (childHoverDebounceRef.current) {
            clearTimeout(childHoverDebounceRef.current);
            childHoverDebounceRef.current = null;
          }
          if (!childHoveredRef.current) {
            childHoveredRef.current = true;
            setChildEditableHovered(true);
          }
        } else if (childHoveredRef.current) {
          if (!childHoverDebounceRef.current) {
            childHoverDebounceRef.current = setTimeout(() => {
              childHoveredRef.current = false;
              setChildEditableHovered(false);
              childHoverDebounceRef.current = null;
            }, 150);
          }
        }
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      style={{
        ...(isTightClickMedia ? { pointerEvents: "none" } : {}),
        ...(formatPainterArmedForThis && !formatPainterIsSource
          ? { cursor: "copy" }
          : {}),
      }}
      // Prevent the browser's native drag on <a>, <img>, etc. inside this element.
      // Without this, the native drag hijacks pointer events from dnd-kit's
      // PointerSensor and dropping the native ghost causes the browser to navigate
      // to the link/image URL — which is what looked like an "instant page reload".
      onDragStart={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        const target = e.target;
        // Quick-action toolbar should never initiate drag or be suppressed.
        if (target?.closest?.("[data-no-dnd='true']")) {
          e.stopPropagation();
          return;
        }
        const isResizeHandle = target.closest('[data-handle]') || target.closest('[data-edge]') || target.closest('[data-overlay-active]');
        const isCropControl = target.closest('[data-crop-control]');
        if (isResizeHandle || isCropControl) {
          // Don't interfere with resize/crop interactions
          return;
        }
        const isInlineEditable = isInlineEditableTarget(target);
        const isNativeInteractive = isNativeInteractiveTarget(target);
        const shouldSuppressNative = !isInlineEditable && isNativeInteractive;
        // In drag-anywhere mode, also suppress native pointer defaults for non-editable
        // targets (e.g. text selection drag on <h1>/<p>) so dnd-kit keeps full control.
        const shouldSuppressForDragAnywhere = dragAnywhereEnabled && !isInlineEditable;
        // Forward to the drag listener so "press anywhere to drag" works.
        // BUT: Don't forward drag listeners for native interactive targets (buttons, links)
        // to ensure they remain fully clickable without drag interference.
        // dnd-kit's PointerSensor will only activate after the pointer has moved
        // the configured distance (8px), but we want buttons/links to work immediately.
        if (dragAnywhereEnabled && dragListeners?.onPointerDown && !isNativeInteractive) {
          dragListeners.onPointerDown(e);
          try {
            editorInteraction?.setDraggablePointerDown?.(true);
          } catch (_) {}
          // Stop propagation so parent EditableElements don't ALSO forward
          // to their own sortable listeners. Without this, nested sortable
          // items (e.g. a title inside a hero) both try to activate in the
          // same DndContext, causing the drag to immediately fail/reload.
          e.stopPropagation();
        }
        // Keep this after dnd-kit listener invocation so we don't accidentally
        // short-circuit drag activation on interactive descendants.
        if (shouldSuppressNative || shouldSuppressForDragAnywhere) {
          e.preventDefault();
        }
      }}
      onMouseDown={(e) => {
        const target = e.target;
        // Quick-action toolbar should never initiate drag or be suppressed.
        if (target?.closest?.("[data-no-dnd='true']")) {
          e.stopPropagation();
          return;
        }
        const isResizeHandle = target.closest('[data-handle]') || target.closest('[data-edge]') || target.closest('[data-overlay-active]');
        const isCropControl = target.closest('[data-crop-control]');
        if (isResizeHandle || isCropControl) {
          // Don't interfere with resize/crop interactions
          return;
        }
        const isInlineEditable = isInlineEditableTarget(target);
        const isNativeInteractive = isNativeInteractiveTarget(target);
        const shouldSuppressNative = !isInlineEditable && isNativeInteractive;
        const shouldSuppressForDragAnywhere = dragAnywhereEnabled && !isInlineEditable;
        // Don't forward drag listeners for native interactive targets (buttons, links)
        // to ensure they remain fully clickable without drag interference.
        if (dragAnywhereEnabled && dragListeners?.onMouseDown && !isNativeInteractive) {
          dragListeners.onMouseDown(e);
          try {
            editorInteraction?.setDraggablePointerDown?.(true);
          } catch (_) {}
          // Mirror the stopPropagation from onPointerDown to prevent
          // nested sortable double-activation via the mouse event path.
          e.stopPropagation();
        }
        if (shouldSuppressNative || shouldSuppressForDragAnywhere) {
          e.preventDefault();
        }
      }}
      className={`
        relative isolate ${dragAnywhereEnabled ? 'cursor-grab' : 'cursor-pointer'} group w-full
        ${isSidebar ? 'h-full' : ''}
      `}
    >
      {/* The actual content - or inline editor when editing */}
      {isCurrentlyInlineEditing && editableField ? (
        <div className="w-full relative">
          <InlineTextEditor
            initialValue={getInlineEditValue(displayElement, editableField)}
            onSave={handleSaveInlineEdit}
            onCancel={handleCancelInlineEdit}
            multiline={safeElement.type === 'text'}
            isHTML={shouldEditAsHTML(safeElement.type)}
            className={`w-full ${inlineEditorStyles.className}`}
            style={inlineEditorStyles.style}
            placeholder={safeElement.type === 'title' ? 'Enter title...' : 'Enter text...'}
          />
        </div>
      ) : (
        // Clone children with displayElement to show optimistic updates
        // This ensures the new text appears immediately after inline editing
        React.isValidElement(children) 
          ? React.cloneElement(children, { item: displayElement })
          : children
      )}

      {renderChromeLayer(
      <>
      {/* Drag handle icon: only shown when drag-anywhere is NOT enabled (legacy handle mode).
         When portaled out of a clipping cover tile there is no `group` ancestor, so
         visibility is driven by hover state instead of CSS group-hover. */}
      {!dragAnywhereEnabled && (
        <DragHandle
          visible={clipsChrome ? (isHovered || chromeHovered) : undefined}
          onMouseEnter={() => setChromeHovered(true)}
          onMouseLeave={() => setChromeHovered(false)}
        />
      )}

      {/* Edit indicator - positioned outside for sections, inside for others.
         Uses <span> instead of <div> so that ancestor [&_div]:!h-full / [&_div]:!w-full
         overrides (used by full-bleed component modes) don't force this badge to fill
         its container and paint a solid bg-blue overlay over the content. */}
      <span
        className={`
        block absolute ${getIndicatorPosition()} p-1 text-xs
        ${colors.indicator} text-white rounded shadow-sm
        transition-opacity duration-200
        ${
          isSelected
            ? "opacity-100"
            : ((isHovered || chromeHovered) ? "opacity-100" : "opacity-0")
        }
        ${isSection ? 'pointer-events-none' : ''}
      `}
      >
        <span className="text-xs tracking-wide font-medium text-white whitespace-nowrap font-sans">{formatTypeName(safeElement.type)}</span>
      </span>

      {/* Quick actions (layout/text/button link/delete), positioned on the top-right border edge.
         Uses <span> instead of <div> so that ancestor [&_div]:!h-full / [&_div]:!w-full
         overrides (used by full-bleed component modes) don't force this toolbar to fill
         its container. */}
      {showTopRightQuickActions && (
        <span
          data-no-dnd="true"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={() => setChromeHovered(true)}
          onMouseLeave={() => setChromeHovered(false)}
          className={`
            block absolute ${isSection ? 'top-full right-0 mt-1' : 'top-1 right-2'} flex max-w-[80vw] ${isSection ? 'flex-nowrap' : 'flex-wrap'} items-center justify-end gap-1 sm:max-w-none
            transition-opacity duration-200 pointer-events-auto
            ${isSelected || isHovered || chromeHovered ? "opacity-100" : "opacity-0 pointer-events-none"}
          `}
          style={{ zIndex: 100 }}
        >
          {showLayoutQuickEdit && (
            <button
              type="button"
              ref={layoutButtonRef}
              onClick={handleLayoutButtonClick}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              className={layoutQuickActionButtonClass}
              aria-label="Edit layout"
              title="Edit layout"
            >
              <LayoutIcon size="sm" />
            </button>
          )}
          {showQuickTextTools && (
            <div
              data-no-dnd="true"
              className={`${textToolsGroupClass} pointer-events-auto`}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  adjustFontSizeAllBreakpoints(2);
                }}
                className={`${textToolsInnerButtonClass} cursor-pointer`}
                aria-label="Increase font size"
                title="Increase font size"
              >
                <FontSizeUpIcon size="sm" />
              </button>
              <div className={textToolsDividerClass} aria-hidden="true" />
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  adjustFontSizeAllBreakpoints(-2);
                }}
                className={`${textToolsInnerButtonClass} cursor-pointer`}
                aria-label="Decrease font size"
                title="Decrease font size"
              >
                <FontSizeDownIcon size="sm" />
              </button>
              <div className={textToolsDividerClass} aria-hidden="true" />
              <InlineQuickColorPicker
                value={getCurrentTextColorHex()}
                onChange={handleQuickColorChange}
                title="Change text color"
                triggerClassName={`${textToolsInnerButtonClass} cursor-pointer`}
                swatchClassName="w-4 h-4 rounded border border-gray-300 dark:border-dark-600"
              />
              <div className={textToolsDividerClass} aria-hidden="true" />
              <InlineFontFamilyPicker
                value={getCurrentTextFontFamily()}
                choices={textFontFamilyChoices}
                onChange={handleQuickFontFamilyChange}
                title="Change font family"
                triggerClassName={`${textToolsInnerButtonClass} cursor-pointer`}
              />
            </div>
          )}
          {showQuickStylePresetTools && (
            <InlineStylePresetPicker
              elementType={safeElement.type}
              onApply={applyStylePreset}
              title="Change style preset"
              triggerClassName={`${stylePresetQuickActionButtonClass} cursor-pointer`}
            >
              <PaletteIcon size="sm" />
            </InlineStylePresetPicker>
          )}
          {showQuickFormatPainter && (
            <button
              type="button"
              data-no-dnd="true"
              onClick={handleFormatPainterClick}
              onDoubleClick={handleFormatPainterDoubleClick}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              className={`${
                formatPainterArmedHere
                  ? formatPainterActiveButtonClass
                  : formatPainterQuickActionButtonClass
              } cursor-pointer`}
              aria-label={
                formatPainterArmedHere
                  ? formatPainterStickyHere
                    ? "Format painter (sticky) — click to cancel"
                    : "Format painter active — click to cancel"
                  : "Format painter"
              }
              title={
                formatPainterArmedHere
                  ? formatPainterStickyHere
                    ? "Sticky painter: click to cancel, Esc to exit"
                    : "Click an element to paste its style"
                  : "Format painter (double-click for sticky)"
              }
              aria-pressed={formatPainterArmedHere}
            >
              <FormatPainterIcon className="w-4 h-4" />
            </button>
          )}
          {showQuickButtonLinkTools && (
            <button
              type="button"
              data-no-dnd="true"
              ref={buttonLinkButtonRef}
              onClick={handleButtonLinkButtonClick}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              className={`${buttonLinkQuickActionButtonClass} ${
                isButtonLinkPopoverOpen
                  ? "bg-gray-100 text-gray-900 dark:bg-dark-700 dark:text-white"
                  : ""
              }`}
              aria-label="Edit link"
              aria-expanded={isButtonLinkPopoverOpen}
              title="Edit link"
            >
              <ExternalLinkIcon size="sm" />
            </button>
          )}
          {showQuickDelete && (
            <button
              type="button"
              onClick={handleDeleteButtonClick}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              className={deleteQuickActionButtonClass}
              aria-label="Delete element"
              title="Delete element"
            >
              <TrashIcon size="sm" />
            </button>
          )}
        </span>
      )}
      </>
      )}

      {showQuickButtonLinkTools && isButtonLinkPopoverOpen && createPortal(
        <div
          ref={buttonLinkPopoverRef}
          data-no-dnd="true"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          className={`
            editor-theme fixed rounded-lg shadow-2xl
            ${surfaces.elevated} ${borders.default}
          `}
          style={{
            ...quickButtonLinkPopoverStyle,
            width: "min(22rem, calc(100vw - 1rem))",
            maxWidth: "calc(100vw - 1rem)",
          }}
        >
          <div className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${borders.default}`}>
            <div className={`flex min-w-0 items-center gap-2 ${typography.bodySmall} ${textColors.primary} font-semibold`}>
              <ExternalLinkIcon size="sm" />
              <span className="truncate">Link</span>
            </div>
            <button
              type="button"
              aria-label="Close link editor"
              className={`rounded-md p-1 ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeQuickButtonLinkPopover();
              }}
            >
              <XIcon size="sm" />
            </button>
          </div>
          <div className="space-y-3 overflow-y-auto px-4 py-3" style={{ maxHeight: "min(70vh, 24rem)" }}>
            <div className="field-wrapper">
              <label className={`mb-2 block ${typography.label} ${textColors.secondary}`}>
                Link Type
              </label>
              <select
                value={currentButtonLinkChoice}
                onChange={handleQuickButtonLinkChoiceChange}
                className={`
                  w-full rounded-md border px-3 py-2 shadow-sm
                  focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                  ${borders.default} ${surfaces.primary} ${textColors.primary} ${typography.body}
                `}
                aria-label="Link Type"
              >
                <option value={BUTTON_LINK_CHOICES.INTERNAL}>Internal page</option>
                <option value={BUTTON_LINK_CHOICES.EXTERNAL}>External URL</option>
              </select>
            </div>

            {currentButtonLinkChoice === BUTTON_LINK_CHOICES.INTERNAL ? (
              <WebsitePageField
                label="Internal Page"
                value={currentButtonInternalPage}
                onChange={handleQuickButtonInternalChange}
                websiteId={websiteId || displayElement?.website || safeElement?.website}
              />
            ) : (
              <div className="field-wrapper">
                <label className={`mb-2 block ${typography.label} ${textColors.secondary}`}>
                  External URL
                </label>
                <input
                  type="url"
                  value={currentButtonExternalUrl}
                  onChange={handleQuickButtonExternalChange}
                  placeholder="https://example.com"
                  className={`
                    w-full rounded-md border px-3 py-2 shadow-sm
                    focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                    ${borders.default} ${surfaces.primary} ${textColors.primary} ${typography.body}
                  `}
                  aria-label="External URL"
                />
              </div>
            )}
          </div>
          <div className={`flex justify-end border-t px-4 py-3 ${borders.default}`}>
            <button
              type="button"
              onClick={handleQuickButtonOpenTarget}
              disabled={buttonLinkAction.disabled}
              title={buttonLinkAction.title}
              className={`
                inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-2 sm:w-auto
                ${typography.button}
                ${interactive.primaryBg}
                ${interactive.primaryHover}
                text-white
                disabled:cursor-not-allowed disabled:opacity-50
                focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
              `}
              aria-label={buttonLinkAction.label}
            >
              <ExternalLinkIcon size="sm" />
              {buttonLinkAction.label}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Format painter source tint — subtle background overlay when this element is
          the armed source of the format painter, so the user knows which style is being copied. */}
      {formatPainterArmedHere && (
        <span
          className={`
            block absolute inset-0 pointer-events-none z-[44]
            transition-all duration-200
            ${formatPainterStickyHere
              ? "bg-amber-300/15 dark:bg-amber-400/10"
              : "bg-blue-300/10 dark:bg-blue-400/8"
            }
          `}
          aria-hidden="true"
        />
      )}

      {/* Hover/selection border overlay — rendered on top of content to stay visible
          even inside ancestor overflow-hidden containers (e.g. components with rounded corners).
          Uses <span> to avoid being targeted by [&_div] full-bleed CSS overrides.
          Skipped entirely when hover border is suppressed and element is not selected,
          preventing any rendering artifact from a transparent 2px border. */}
      {(isSelected || showHoverBorder || (!suppressHoverBorder && !inheritedSuppressHoverBorder)) && (
        <span
          className={`
            block absolute inset-0 pointer-events-none z-[45]
            border-2 transition-colors duration-150
            ${colors.border}
          `}
        />
      )}

      {/* Quick layout modal - portaled to body to ensure proper z-index stacking */}
      {showLayoutQuickEdit && isLayoutModalOpen && createPortal(
        <div
          ref={layoutModalRef}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={`
            editor-theme fixed w-[340px] max-w-[80vw]
            ${surfaces.elevated} ${borders.default} rounded-lg shadow-2xl
          `}
          style={{
            zIndex: 9999,
            top: layoutButtonRef.current
              ? `${layoutButtonRef.current.getBoundingClientRect().bottom + 8}px`
              : '50%',
            right: layoutButtonRef.current
              ? `${window.innerWidth - layoutButtonRef.current.getBoundingClientRect().right}px`
              : '20px',
          }}
        >
          <div className={`flex items-center justify-between px-4 py-3 border-b ${borders.default}`}>
            <div className={`flex items-center gap-2 ${typography.bodySmall} ${textColors.primary} font-semibold`}>
              <LayoutIcon size="md" color="primary" />
              <span>Layout</span>
            </div>
            <button
              type="button"
              aria-label="Close overlay"
              className={`p-1 rounded-md ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`}
              onClick={(event) => {
                event.stopPropagation();
                closeQuickLayout();
              }}
            >
              <XIcon size="sm" />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
            <LayoutByLayoutField
              value={layoutDraft || layoutBaseline || {}}
              onChange={(nextValue) => setLayoutDraft(nextValue)}
              label=""
              help="Adjust how this component arranges its children per breakpoint."
            />
          </div>
          <div className={`flex items-center justify-end gap-2 px-4 py-3 border-t ${borders.default}`}>
            <button
              type="button"
              className={`
                inline-flex items-center gap-1 px-3 py-2 rounded-md
                ${typography.button}
                ${surfaces.tertiary}
                ${textColors.primary}
                ${surfaces.hover}
                focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
              `}
              onClick={(event) => {
                event.stopPropagation();
                closeQuickLayout();
              }}
            >
              <XIcon size="sm" />
              Cancel
            </button>
            <button
              type="button"
              disabled={!hasLayoutChanges || isSavingLayout}
              className={`
                inline-flex items-center gap-1 px-3 py-2 rounded-md
                ${typography.button}
                ${interactive.primaryBg}
                ${interactive.primaryHover}
                text-white
                disabled:opacity-50 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
              `}
              onClick={(event) => {
                event.stopPropagation();
                handleQuickLayoutSave();
              }}
            >
              <CheckIcon size="sm" />
              {isSavingLayout ? "Saving..." : "Save"}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  return shouldPropagateSuppress ? (
    <SuppressChildHoverBorderContext.Provider value={true}>
      {wrapperJsx}
    </SuppressChildHoverBorderContext.Provider>
  ) : wrapperJsx;
};

// Back-compat for tests and callsites that import a named export.
export { EditableElement };

const DragHandle = ({ visible, onMouseEnter, onMouseLeave }) => {
  const ctx = useDragHandle();
  // If there's no sortable context (not draggable), render nothing
  if (!ctx || !ctx.dragListeners) return null;
  const { dragListeners } = ctx;
  // Rendered as <span> (not <div>) for the same reason as the indicator,
  // quick-action toolbar, format-painter tint and selection-border overlay:
  // full-bleed/cover component modes apply ancestor [&_div]:!h-full / [&_div]:!w-full
  // overrides to fill tiles with media. A <div> handle would be inflated to the
  // full tile size, flooding it with the dark bg-gray-800/80 pill ("shadow") and
  // covering the blue hover/selection outline. A <span> is exempt from [&_div]
  // overrides, so the handle stays a small pill in its corner.
  //
  // Visibility: by default the handle reveals on CSS `group-hover` (legacy inline
  // mode). When the chrome is portaled out of a clipping cover tile, there is no
  // `group` ancestor in the portal, so callers pass an explicit `visible` boolean
  // (driven by hover state) instead.
  const visibilityClass =
    visible === undefined ? "hidden group-hover:flex" : visible ? "flex" : "hidden";
  return (
    <span
      className={`absolute -top-3 right-2 z-20 ${visibilityClass} items-center gap-1 px-1 py-0.5 rounded bg-gray-800/80 dark:bg-dark-900/90 !text-white pointer-events-auto ${typography.caption}`}
      title="Drag to reorder"
      style={{ cursor: 'grab' }}
      {...dragListeners}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      ⋮⋮
    </span>
  );
};

export default EditableElement;
